<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { showModal } from '@/Utils/modalBus'
import PaywallService from "@/services/PaywallService";
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification";
import FormInput from '../Atoms/FormInput.vue';
import FormButton from '../Atoms/FormButton.vue';
import FormUpload from '../Atoms/FormUpload.vue';
import Alert from '../Atoms/Alert.vue';
import { useRoute, useRouter } from 'vue-router'

const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()
const store = useStore()
const { notify } = useNotification()

const companyName = ref(null)
const companyUrl = ref(null)
const companyEmail = ref(null)

// Logo — a brand asset shown on the paywall and receipts, so it lives with the
// company/brand details.
const logoUrlTmp = ref(null)
const logoSrc = ref(null)
const logoProcessing = ref(false)
const errorMsg = ref(null)
const logoUrl = computed(() => logoUrlTmp.value ? logoUrlTmp.value : logoSrc.value)

const props = defineProps({
  currentPaywall: {
    type: Function,
  }
})

const emit = defineEmits({
  updated: () => true,
})

onMounted(() => {
  companyName.value = props.currentPaywall?.company_name
  companyUrl.value = props.currentPaywall?.company_url
  companyEmail.value = props.currentPaywall?.company_email
  logoSrc.value = props.currentPaywall?.company_logo
})

const changeCompanyInfo = async (prop) => {
  if (prop === 'companyUrl') {
    const isUrl = string => {
      try {
        return Boolean(new URL(string));
      }
      catch (e) { return false; }
    }

    if (!isUrl(companyUrl.value)) {
      notify({
        group: 'error',
        text: "Enter a valid company website URL",
      })
      return
    }
  }

  try {
    const value = (prop === 'companyUrl') ? companyUrl.value
      : (prop === 'companyName')
        ? companyName.value
        : companyEmail.value
    const obj = {}

    obj[(prop === 'companyUrl')
      ? 'company_url'
      : (prop === 'companyName')
        ? 'company_name'
        : 'company_email'] = value
    await PaywallService.updateStreamPaywallById(props.currentPaywall.id, obj)
    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop, value })
    await store.dispatch('Paywalls/getPaywalls', { sort: 'desc', forceRefresh: true })
    emit('updated')
    notify({
      group: "success",
      text: "Company details updated",
    });

  } catch (err) {
    console.log(err);
    notify({
      group: 'error',
      text: err.message || "Couldn't update company details",
    })
  }
}

const onLogoPreview = (file) => {
  const selector = "#logo-image-input";
  const imageInput = document.querySelector(selector);
  const imageFile = file;

  if (!imageFile) return;

  if (!["image/png", "image/jpeg"].includes(imageFile.type)) {
    showModal("embed-upload-format");
    imageInput.value = null;
    return;
  }

  const maxSize = 1 * 1024 * 1024;
  if (imageFile.size > maxSize) {
    showModal("embed-image-size-exceed");
    imageInput.value = null;
    return;
  }

  imageReader(imageFile, (base64) => {
    logoUrlTmp.value = base64;
  });
}

const saveLogo = async () => {
  const selector = "#logo-image-input";
  const imageInput = document.querySelector(selector);
  const imageFile = imageInput.files[0];

  if (!imageInput.value || !imageFile) {
    errorMsg.value = "Please select an image file.";
    return;
  }

  logoProcessing.value = true;

  const fdata = new FormData();
  fdata.append("file", imageFile);

  try {
    const res = await PaywallService.paywallUploadLogo(fdata, props.currentPaywall.id);
    logoUrlTmp.value = null;
    logoSrc.value = res.logoUrl;
    errorMsg.value = null;

    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop: 'company_logo', value: res.logoUrl })
    emit('updated')

    if (imageInput) imageInput.value = '';

    notify({ group: "success", text: `Logo uploaded` });
  } catch (err) {
    errorMsg.value = "Couldn't upload the image. Please try again.";
    notify({ group: "error", text: err.message || "Couldn't upload logo" });
  }

  logoProcessing.value = false;
}

function imageReader(file, cb) {
  if (!file) return;
  const reader = new FileReader();
  reader.onload = function () {
    if (cb) cb(reader.result);
  };
  reader.readAsDataURL(file);
}

const cancelUpload = async () => {
  logoProcessing.value = true;
  const selector = "#logo-image-input";
  const previewFlag = logoUrlTmp.value;
  errorMsg.value = null;
  if (previewFlag) {
    logoUrlTmp.value = null;
    document.querySelector(selector).value = null;
    logoProcessing.value = false;
    return;
  }
  try {
    await PaywallService.updatePaywallSettings({ company_logo: null }, props.currentPaywall.id);
    logoSrc.value = null;
    store.dispatch('Paywalls/setPaywallProp', { id: props.currentPaywall.id, prop: 'company_logo', value: null })
    emit('updated')
  } catch (e) {
    console.error("paywall remove logo failed", e);
    notify({ group: "error", text: "Couldn't save changes" });
  }
  logoProcessing.value = false;
}
</script>

<template>
  <section class="bg-surface-1 rounded-xl p-4 sm:p-5">
    <div class="mb-4">
      <h3 class="text-sm font-semibold text-white mb-0.5">Company &amp; brand</h3>
      <p class="text-tiny text-surface-7 mb-0">Displayed to viewers on your paywall and payment receipts. Leave a field
        blank to inherit your <router-link :to="{ path: '/paywall/v2', query: { view: 'settings' } }" class="text-main hover:underline">account
          defaults</router-link>.</p>
    </div>
    <div class="flex flex-col gap-y-4">
      <FormInput label="Company name" v-model="companyName" placeholder="Inherit from account defaults" :maxlength="100"
        @change="changeCompanyInfo('companyName')" />
      <FormInput label="Company website" v-model="companyUrl" placeholder="Inherit from account defaults" :maxlength="200"
        @change="changeCompanyInfo('companyUrl')" />
      <FormInput label="Support email" v-model="companyEmail" placeholder="Inherit from account defaults" :maxlength="150"
        @change="changeCompanyInfo('companyEmail')" />
    </div>

    <!-- Logo -->
    <div class="flex flex-col gap-y-2 items-start mt-4 pt-4 border-t border-surface-3">
      <div>
        <h4 class="text-sm font-medium text-white mb-0.5">Logo</h4>
        <p class="text-tiny text-surface-7 mb-0">Recommended: 120 × 50 px · PNG or JPG · up to 1 MB.</p>
      </div>
      <div class="flex gap-x-2 items-center w-full mt-1">
        <img v-if="logoUrl" :src="logoUrl" alt="Paywall logo preview"
          class="h-[50px] max-w-[120px] object-contain rounded bg-surface-2 p-1">
        <p v-else class="text-tiny text-surface-6">No logo uploaded</p>
        <FormUpload class="ml-auto" name="endEmbedPoster" id="logo-image-input" placeholder="No file chosen"
          acceptType="image/png,image/jpeg" :label="logoSrc ? 'Change' : 'Upload logo'" type="secondary" size="sm"
          :on-file-change="onLogoPreview" />
        <FormButton v-if="logoUrlTmp" :label="logoProcessing ? 'Saving…' : 'Save'" :disabled="logoProcessing"
          size="sm" @click="saveLogo" />
        <FormButton v-if="logoUrlTmp || logoSrc" :disabled="logoProcessing" type="secondary" size="sm"
          @click="cancelUpload">
          <span v-if="logoSrc && logoUrlTmp">Restore</span>
          <span v-else-if="logoUrlTmp">Cancel</span>
          <span v-else>Remove</span>
        </FormButton>
      </div>
      <Alert type="error" v-show="errorMsg" :text="errorMsg" />
    </div>
  </section>
</template>

<style scoped></style>
